import { ownedBy, territoryDef } from './helpers';
import { chance, pick, rollD6 } from './rng';
import { rulesFor } from './rules';
import type { GameEvent, GameState, MapDef, PlayerId, TerritoryId } from './types';

function spawnChance(state: GameState, zombies: number): number {
  const rules = rulesFor(state.config.difficulty);
  const mutation =
    state.round < state.modifiers.mutationUntilRound ? state.modifiers.mutationSteps : 0;
  return Math.min(
    rules.spawnCap,
    rules.baseSpawn + rules.spawnPerZombie * zombies + rules.mutationStep * mutation,
  );
}

function quarantined(state: GameState): boolean {
  return state.round < state.modifiers.quarantineUntilRound;
}

/** Horde territories with no zombies left become neutral. */
export function normalizeHorde(state: GameState, id: TerritoryId): void {
  const t = state.territories[id]!;
  if (t.owner === 'horde' && t.zombies === 0) t.owner = null;
  if (t.owner === null && t.zombies > 0) t.owner = 'horde';
}

/**
 * Zombie Rising for one player's territories: spawn, bite, overrun.
 * Mutates `state` (callers pass a fresh copy) and appends events.
 */
export function playerRising(
  map: MapDef,
  state: GameState,
  playerId: PlayerId,
  events: GameEvent[],
): void {
  const rules = rulesFor(state.config.difficulty);
  events.push({ type: 'risingStarted', playerId, round: state.round });

  const sirenIndex = state.modifiers.sirenPlayers.indexOf(playerId);
  const rolls = sirenIndex >= 0 ? 2 : 1;
  if (sirenIndex >= 0) state.modifiers.sirenPlayers.splice(sirenIndex, 1);

  const owned = ownedBy(state, playerId).sort();
  for (const id of owned) {
    const t = state.territories[id]!;

    if (!quarantined(state)) {
      let spawned = 0;
      for (let i = 0; i < rolls; i++) {
        let hit: boolean;
        [hit, state.rng] = chance(state.rng, spawnChance(state, t.zombies));
        if (hit) spawned++;
      }
      if (spawned > 0) {
        const count = spawned * (1 + Math.floor(t.zombies * rules.spawnBurst));
        t.zombies += count;
        events.push({ type: 'zombiesSpawned', territoryId: id, count });
      }
    }

    const immune = (t.immuneUntilRound ?? 0) > state.round;
    if (t.zombies > 0 && t.armies > 0 && !immune) {
      let hits = 0;
      const attacks = Math.min(t.zombies, t.armies);
      for (let i = 0; i < attacks; i++) {
        let v: number;
        [v, state.rng] = rollD6(state.rng);
        if (v >= rules.biteThreshold) hits++;
      }
      if (hits > 0) {
        t.armies -= hits;
        events.push({ type: 'bitten', territoryId: id, count: hits });
      }
    }

    if (t.zombies > t.armies) {
      if (t.armies > 0) events.push({ type: 'armiesLost', territoryId: id, count: t.armies });
      t.armies = 0;
      events.push({ type: 'overrun', territoryId: id, previousOwner: t.owner });
      t.owner = 'horde';
    }
    if (t.immuneUntilRound !== undefined && t.immuneUntilRound <= state.round)
      delete t.immuneUntilRound;
  }
}

/**
 * Round end. Every horde territory grows a little. On assault rounds each
 * stack pushes a share of itself into a random neighbour: an overrun if the
 * defenders are thin, a fight to purge otherwise.
 */
export function hordeRoundEnd(map: MapDef, state: GameState, events: GameEvent[]): void {
  const rules = rulesFor(state.config.difficulty);
  events.push({ type: 'risingStarted', playerId: null, round: state.round });
  if (quarantined(state)) return;

  const hordeTerritories = ownedBy(state, 'horde').sort();
  for (const id of hordeTerritories) {
    const t = state.territories[id]!;
    const growth = Math.max(rules.hordeGrowth, Math.floor(t.zombies * rules.hordeGrowthRate));
    t.zombies += growth;
    events.push({ type: 'zombiesSpawned', territoryId: id, count: growth });
  }

  if (state.round % rules.spreadEvery !== 0) return;
  for (const id of hordeTerritories) {
    const src = state.territories[id]!;
    const candidates = territoryDef(map, id).neighbors.filter(
      (n) => state.territories[n]!.owner !== 'horde',
    );
    if (candidates.length === 0) continue;
    let target: TerritoryId;
    [target, state.rng] = pick(state.rng, candidates);
    const moving = Math.max(1, Math.floor(src.zombies * rules.assaultFraction));
    src.zombies -= moving;
    const t = state.territories[target]!;
    t.zombies += moving;
    normalizeHorde(state, id);
    normalizeHorde(state, target);
    events.push({ type: 'hordeSpread', from: id, to: target });
    events.push({ type: 'zombiesMoved', from: id, to: target, count: moving });
  }
}
